import {AbstractContainer} from "./AbstractContainer";
import {dateFromStr, formatDate, TimeCalculator} from "../time/TimeCalculator";
import * as $ from "jquery";
import {
    GeometryTypeEnum,
    IndicatorChartTypeEnum,
    PeriodicityEnum,
    StyleRuleTypeEnum,
    TimeScaleEnum
} from "../enum/enum";
import {Loader} from "../ui/Loader";
import Plotly from 'plotly.js-dist-min';
import {Dropdown, DropdownOption} from "../ui/Dropdown";
import '/modules/common/css/timeseries/indicator.css';
import '/modules/common/css/timeseries/indicator.responsive.css';
import '/modules/common/css/timeseries/indicator.mobile.css';
import {arrayLast, clone, formatNumber} from "../util";
import {ChartStyler} from "../layers/styles";
import {Style} from "../config/Style";
import {Legend} from "../layers/Legend";
import {BoxPlot} from "../ui/BoxPlot";
import {ClassifiedSummaryStats} from "./ClassifiedSummaryStats";

/**
 * An indicator chart
 * @extends AbstractContainer
 */
class IndicatorChart extends AbstractContainer {
    getClassName(){
        return "IndicatorChart";
    }

    /**
     *
     * @param id {string} An HTML id for this indicator chart
     * @param translator {Translator} A translator
     * @param dataSeriesUrl {string}
     * @param layer {Layer} The indicator of this chart
     * @param infoModal {Modal} A modal window to show indicator metadata in
     * @param runtimeConfig {RuntimeConfig} The runtime config
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     */
    constructor(id, translator, dataSeriesUrl, layer, infoModal, runtimeConfig, isMobileDevice) {
        super(id, translator, isMobileDevice, runtimeConfig);
        this.layer = layer;
        this.infoModal = infoModal;
        this.renderedChart = false;
        this.dataSeriesUrl = dataSeriesUrl;
    }

    /**
     * @param from {Date}
     * @param until {Date}
     * @param wgs84Coords {Array<number>} 2 position array with coords lon lat in wgs84
     */
    setFromUntilAndCoords(from, until, wgs84Coords) {
        const timeCalculator = this.layer.getTimeCalculator();
        if (from) {
            this.from = timeCalculator.fixDate(dateFromStr(from));
            this.unboundedFrom = timeCalculator.min(dateFromStr(from), this.from);
        }
        if (until) {
            if(timeCalculator.isMonthlyUpdatedTenDaily() || timeCalculator.isTenDailyYearless30Day()){
                const end = timeCalculator.getEndDateOfPeriod(until);
                this.until = timeCalculator.getLastDayOfMonthFromDate(timeCalculator.getEndDateOfPeriod(end));
                this.unboundedUntil = this.until;
            } else {
                this.until = timeCalculator.fixUnboundedDateUntil(dateFromStr(until));
                this.unboundedUntil = timeCalculator.max(dateFromStr(until), this.until);
            }
        }
        if (wgs84Coords && (!this.wgs84Coords || this.wgs84Coords[0] !== wgs84Coords[0] || this.wgs84Coords[1] !== wgs84Coords[1]))
            this.timeseriesFetcher = this.layer.getTimeseriesFetcher(this.dataSeriesUrl, wgs84Coords);
        this.wgs84Coords = wgs84Coords;
        this._updateChart();
    }

    expand() {
        super.expand();
        if (this.renderedChart) {
            this._relayout();
        }
    }

    _relayout() {
        const chartTarget = $('.container .body .chart-container', this.uiElement);
        Plotly.relayout(chartTarget[0], {autosize: true});
    }

    _updateChart() {
        this._generateChartElements();
        this._updateFigCaptionContent();
    }

    /**
     * Generate the actual chart elements (and append them to the container elements)
     * @protected
     */
    _generateChartElements() {
        $('.container .body .chart-container', this.uiElement).empty();
        $('.container .body .legend-container', this.uiElement).empty();
        this.renderedChart = false;
        this.loader.show(false);
        this.timeseriesFetcher.loadData(this.from, this.until, this.unboundedFrom, this.unboundedUntil, (data, isRandomData) => {
            if (data) {
                const timeScales = this.layer.getServiceConfig().getTimeScales();
                const timeCalculator = this.layer.getTimeCalculator();
                const filterTenDayPeriods = (data) => {
                    const tenDayPeriod = Number(this._getTenDayPeriod());
                    const xValues = [], yValues = [];
                    for (let i = 0; i < data.x.length; i++) {
                        const x = data.x[i];
                        if (timeCalculator.getTenDaysFromDate(x) === tenDayPeriod) {
                            xValues.push(timeCalculator.getStartDateOfPeriod(x));
                            yValues.push(data.y[i]);
                        }
                    }
                    data.x = xValues;
                    data.y = yValues;
                    return data;
                }
                const prodCode = timeScales && timeScales.length > 0
                    ? this.layer.getProductConfigForTimeScale(this._getTimeScale()).getCode()
                    : this.layer.getProductConfig().getCode();
                const data2Render = timeCalculator.isMonthlyUpdatedTenDaily() || timeCalculator.isTenDailyYearless30Day() ? filterTenDayPeriods(data[prodCode]) : data[prodCode];
                this._renderChart([data2Render], isRandomData, undefined);
                const downloadButton = $('<button class="download-data-button transparent" title="' + this.translator.translate('tool.charts.download.csv') + '"></button>');
                downloadButton.click(() => this._downloadData(prodCode));
                $('.body .indicator-buttons-container', this.uiElement).empty().append(downloadButton);
            } else {
                $('.body .indicator-buttons-container', this.uiElement).empty();
                $('.container .body .chart-container', this.uiElement).append($(
                    '<div class="no-layers">Error loading data</div>'
                ));
            }
            this.loader.hide();
        });
    }

    /**
     * Get a simple stats shower for this indicator chart
     * @param data {Array<number|string>} The data to calculate stats on
     * @return {AbstractSimpleStatsShower}
     * @private
     */
    _getSimpleStatsShower(data) {
        const chartStyler = this.layer.getChartStyler();
        const isClassified = chartStyler && chartStyler.isClassified();
        let simpleStatsShower;
        if (isClassified) {
            $('.container .body', this.uiElement).addClass('has-simple-stats');
            simpleStatsShower = new ClassifiedSummaryStats(
                chartStyler,
                data,
                'indicator_' + this.layer.getCode() + '_classifiedstats',
                this.translator,
                this.isMobileDevice,
                this.runtimeConfig,
                this.onChangeRuntimeConfig
            );
        } else {
            $('.container .body', this.uiElement).addClass('has-box-plot').addClass('has-simple-stats');
            simpleStatsShower = new BoxPlot(
                this.layer.getNrOfDecimals(),
                data,
                'indicator_' + this.layer.getCode() + '_boxplot',
                this.translator,
                this.isMobileDevice,
                this.runtimeConfig,
                this.onChangeRuntimeConfig
            );
        }
        return simpleStatsShower;
    }

    _renderChart(data, isRandomData, annotationDate) {
        const timeCalculator = this.layer.getTimeCalculator();

        const isClassified = this.layer.getChartStyler() && this.layer.getChartStyler().isClassified();
        if (isClassified && data[0].y) {
            data[0].y = data[0].y.map(v => v !== null && v !== undefined && !(v instanceof Object) ? v.toString() : v);
        }

        const chartType = this.layer.layerConfig.getChartType();
        let accumulatedData = undefined;
        const isBarsWithLineAndAccumulation = chartType === IndicatorChartTypeEnum.BARS_WITH_LINE_AND_ACCUMULATION;
        const isBarsWithLine = isBarsWithLineAndAccumulation || chartType === IndicatorChartTypeEnum.BARS_WITH_LINE;
        const isStackedBars = chartType === IndicatorChartTypeEnum.STACKED_BARS;
        const simpleStatsShower = isBarsWithLine
            ? this._getSimpleStatsShower(data.values[0].y)
            : this._getSimpleStatsShower(data[0].y);
        if (isBarsWithLine) {
            this.yValues = data.values[0].y.slice();
            this.yAvgValues = data.values[1].y.slice();
            this.yStdValues = data.values[2].y.slice();
            if (isBarsWithLineAndAccumulation) {
                this.yAccumValues = data.accumulated[0].y.slice();
                this.yAccumAvgValues = data.accumulated[1].y.slice();
                this.yAccumStdValues = data.accumulated[2].y.slice();
                accumulatedData = data.accumulated;
                data = data.values;
            }
        } else {
            this.yValues = data[0].y.slice();
        }
        this.xValues = data[0].x.slice();

        if (simpleStatsShower) {
            $('.container .body', this.uiElement).addClass('has-simple-stats');
            $('.container .body .simple-stats-container', this.uiElement).empty().append(simpleStatsShower.getUIElement())
        }

        const width = this._calcBarWidth(data[0].x);
        const offset = 0;
        data[0].width = width;
        data[0].offset = offset;

        const rangeEnd = timeCalculator.isMonthlyUpdatedTenDaily() || timeCalculator.isTenDailyYearless30Day()
                ? timeCalculator.getLastDayOfMonthFromDate(this.until)
                : timeCalculator.getEndDateOfPeriod(arrayLast(data[0].x));
        const layout = {
            hovermode: 'x unified',
            hoverlabel: {bgcolor: "#EEEEEE"},
            autosize: true,
            height: 185,
            margin: {
                l: isStackedBars ? (this.isSmallContainerWidth ? 20 : 30) : (this.isSmallContainerWidth ? 45 : 60),
                r: 15,
                b: 35,
                t: 5,
                pad: 2
            },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            showlegend: false,
            dragmode: false,
            bargap: 0,
            xaxis: {
                type: 'date',
                range: [data[0].x[0], rangeEnd],
                zerolinewidth: 0.5,
                zerolinecolor: 'rgb(200,200,200)',
                gridcolor: 'rgba(200,200,200,0.5)',
                linecolor: 'rgba(0,0,0,0)',
                linewidth: 0,
                showspikes: true,
                spikemode: 'toaxis+across+marker',
                spikesnap: 'hovered data',
                spikedash: 'dot',
                spikethickness: -2, //hack to remove halo
                spikecolor: 'rgba(40,110,159,.5)',
                tickmode: 'array',
                tickfont: {size: this.isSmallContainerWidth ? 8 : 11}
            },
            yaxis: {
                zerolinewidth: 0.5,
                zerolinecolor: 'rgb(200,200,200)',
                gridcolor: 'rgba(200,200,200,0.5)',
                linecolor: 'rgba(0,0,0,0)',
                linewidth: 0,
                showspikes: true,
                spikemode: 'toaxis+across',
                spikesnap: 'cursor',
                spikedash: 'dot',
                spikethickness: -2, //hack to remove halo
                spikecolor: 'rgba(40,110,159,.5)',
                tickfont: {size: this.isSmallContainerWidth ? 8 : 11}
            }
        };
        if (annotationDate) {
            const firstDate = this.layer.getTimeCalculator().getStartDateOfPeriod(annotationDate);
            const secondDate = this.layer.getTimeCalculator().getEndDateOfPeriod(annotationDate);
            layout.shapes = [{
                type: 'rect',
                x0: firstDate,
                x1: secondDate,
                xref: 'x',
                y0: 0,
                y1: 1,
                yref: 'paper',
                opacity: .55,
                fillcolor: 'rgba(0,0,0,0)',
                line: {
                    dash: 'dash',
                    width: .5,
                    opacity: .5,
                    color: 'rgb(40,110,159)'
                }
            }];
        }

        const config = {
            responsive: true,
            showSendToCloud: false,
            modeBarButtonsToRemove: ['resetscale', 'select2d', 'lasso2d', 'zoom2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'pan2d'],
            modebarStyle: {
                orientation: this.isMobileDevice ? 'v' : 'h'
            }
        };

        const handleBarData = (data, layout, isAccumulated = false) => {
            if (data[0].x.length <= 0 || data[0].y.length <= 0)
                return undefined;

            const xHoverTexts = timeCalculator.isTenDaily() || timeCalculator.isTenDailyYearless() || timeCalculator.isWeekly() || timeCalculator.isISOWeekly()
                ? data[0].x.map(x => timeCalculator.timeCodeToString(x))
                : timeCalculator.isMonthlyUpdatedTenDaily() || timeCalculator.isTenDailyYearless30Day()
                    ? data[0].x.map(x => timeCalculator.timeCodeToString(timeCalculator.getNextDate(timeCalculator.getNextDate(x))))
                    : [];
            const generateXLastPoint = (data) => {
                data.x.push(this.layer.getTimeCalculator().getNextDate(arrayLast(data.x)) - 10);
            }
            const generateYLastPoint = (data) => {
                data.y.push(arrayLast(data.y));
            }

            data[0].type = 'bar';
            data[0].name = this.getStatsSeriesName();
            data[0].showlegend = false;

            if (timeCalculator.isTenDaily() || timeCalculator.isTenDailyYearless() || timeCalculator.isWeekly()
                || timeCalculator.isISOWeekly() || timeCalculator.isMonthlyUpdatedTenDaily()|| timeCalculator.isTenDailyYearless30Day()) {
                $('.chart-container', this.uiElement).addClass('periodicity-' + this.layer.getTimeCalculator().getPeriodicity().getCode());
                data[0].hovertemplate = '%{hovertext}<extra></extra>';
                data[0].hovertext = [];
                for (let i = 0; i < data[0].x.length; i++) {
                    data[0].hovertext.push(xHoverTexts[i] + '<br>' + formatNumber(data[0].y[i], this.layer.getNrOfDecimals()))
                }
                layout.hovermode = 'x unified';
                layout.xaxis.hoverformat = ' ';
            } else {
                data[0].hovertemplate = '%{y:.' + this.layer.getNrOfDecimals() + 'f}<extra></extra>';
            }

            if (timeCalculator.isMonthly() || timeCalculator.isMonthlyForecast()) {
                layout.xaxis.tickformat = '%B\n %Y';
            } else if (timeCalculator.isYearly()) {
                layout.xaxis.tickformat = '%Y';
            } else {
                layout.xaxis.tickformat = '%d %B\n %Y';
            }

            data[0].marker = {
                line: {
                    color: 'rgba(0,0,0,.25)',
                    width: isAccumulated ? .15 : 0.05
                }
            }
            const chartStyler = this.layer.getChartStyler();
            if (!(isAccumulated && isBarsWithLine) && chartStyler) {
                const colorFunction = chartStyler.getColorFunction();
                if (colorFunction) {
                    data[0].marker.color = data[0].y.map(v => v !== null && v !== undefined ? colorFunction(v) : 'rgba(0,0,0,0)');
                }
            }

            let yAxisLabel;
            if (isAccumulated) {
                const accumulatedChartStyler = this.layer.getAccumulatedChartStyler();
                yAxisLabel = accumulatedChartStyler ? this.translator.translate(accumulatedChartStyler.getYAxisLabel()) : undefined;
            } else {
                yAxisLabel = chartStyler ? this.translator.translate(chartStyler.getYAxisLabel()) : undefined;
            }
            if (yAxisLabel) {
                layout.yaxis.title = {
                    text: yAxisLabel.crop(25),
                    standoff: 10
                };
                layout.yaxis.titlefont = {
                    size: this.isSmallContainerWidth ? 10 : 13,
                    color: '#888888'
                }
            }

            if (isAccumulated && isBarsWithLine && chartStyler) {
                const accumulatedChartStyler = this.layer.getAccumulatedChartStyler();
                if (accumulatedChartStyler) {
                    const accumulatedColorFunction = accumulatedChartStyler.getColorFunction();
                    data[0].marker.color = [];
                    data[0].text = [];
                    for (let i = 0; i < data[0].y.length; i++) {
                        const value = data[0].y[i];
                        const diff = value - data[1].y[i];
                        data[0].marker.color.push(accumulatedColorFunction(diff));
                        const sign = diff > 0 ? '+' : '';
                        data[0].text.push(value.toFixed(this.layer.getNrOfDecimals()) + ' | ' + sign + diff.toFixed(this.layer.getNrOfDecimals()));
                    }
                    data[0].legend = new Legend(this.isMobileDevice, this.translator, [
                        this.layer.layerConfig.allStyles[this.layer.layerConfig.getAccumulatedStyleCode()]
                    ]);
                } else {
                    const minMaxColor = chartStyler.getMinMaxColor();
                    let minValue = Number.MAX_VALUE, maxValue = Number.MIN_VALUE;
                    for (let i = 0; i < data[0].y.length; i++) {
                        const diff = data[0].y[i] - data[1].y[i];
                        if (diff < minValue)
                            minValue = diff;
                        if (diff > maxValue)
                            maxValue = diff;
                    }
                    if (maxValue - minValue > 10) {
                        maxValue = Math.ceil(maxValue);
                        minValue = Math.floor(minValue);
                    }
                    if (minMaxColor) {
                        const style = new Style({
                            code: 'continuous_accum_' + this.layer.getCode(),
                            geometryType: GeometryTypeEnum.POLYGON.getCode(),
                            attribute: "dummy",
                            styleRules: [
                                {
                                    code: 'dummy',
                                    type: StyleRuleTypeEnum.CONTINUOUS.getCode(),
                                    label: this.translator.translate('tool.charts.accumulated.styleLabel'),
                                    ranges: [{
                                        minValue: minValue,
                                        maxValue: maxValue,
                                        minLabel: minValue.toFixed(this.layer.getNrOfDecimals()),
                                        maxLabel: maxValue.toFixed(this.layer.getNrOfDecimals()),
                                        minBackgroundColor: minMaxColor[1],
                                        maxBackgroundColor: minMaxColor[0]
                                    }]
                                }
                            ]
                        });
                        const dynamicStyler = new ChartStyler(style);
                        data[0].legend = new Legend(this.isMobileDevice, this.translator, [style]);
                        const dynamicColorFunction = dynamicStyler.getColorFunction();
                        const colors = [];
                        for (let i = 0; i < data[0].y.length; i++) {
                            const diff = data[0].y[i] - data[1].y[i];
                            colors[i] = dynamicColorFunction(diff);
                        }
                        data[0].marker.color = colors;
                    }
                }
            }

            const isClassified = this.layer.getChartStyler() && this.layer.getChartStyler().isClassified();
            if (isClassified) {
                const labelFunction = this.layer.getChartStyler().getLabelFunction();
                data[0].hovertemplate = '%{hovertext}<extra></extra>';
                data[0].hovertext = timeCalculator.isTenDaily() || timeCalculator.isTenDailyYearless() || timeCalculator.isWeekly() || timeCalculator.isISOWeekly()
                    ? []
                    : data[0].y.map(value => (labelFunction ? labelFunction(value) : value));
                for (let i = 0; i < data[0].x.length; i++) {
                    data[0].hovertext.push(xHoverTexts[i] + '<br>' + (labelFunction ? labelFunction(data[0].y[i]) : data[0].y[i]))
                }
                data[0].originalY = data[0].y.slice();
                data[0].y = data[0].y.map(value => value !== null && value !== undefined ? 1 : value);
                layout.xaxis.showgrid = false;
                layout.xaxis.showspikes = true;
                layout.yaxis.showgrid = false;
                layout.yaxis.showticklabels = false;
                layout.yaxis.range = [0, 1.5];
                layout.yaxis.showspikes = false;
                layout.height = 100;
                layout.margin.t = 0;
            }

            if (isBarsWithLine) {
                if (data.length > 1) {
                    data[1].type = 'line';
                    data[1].mode = 'lines';
                    data[1].legendgroup = 'stats';
                    if (!data[0].text)
                        data[0].text = data[0].y.map((val, idx) => val ? (val.toFixed(this.layer.getNrOfDecimals()) + ' (' + (val - data[1].y[idx]).toFixed(this.layer.getNrOfDecimals()) + ')') : '');
                    generateXLastPoint(data[1]);
                    generateYLastPoint(data[1]);
                    data[1].width = width;
                    data[1].offset = offset;
                    data[1].name = this.getStatsSeriesName(isAccumulated);
                    data[1].showlegend = true;
                    data[1].hovertemplate = '%{y:.' + this.layer.getNrOfDecimals() + 'f}<extra></extra>';
                    data[0].hovertemplate = '%{text}<extra></extra>';
                    data[0].textposition = 'none';
                    data[1].marker = {
                        color: '#ff6600'
                    };
                    data[1].line = {
                        width: 1.5
                    };
                    layout.showlegend = true;
                    layout.legend = {
                        orientation: 'h', xanchor: "center", x: 0.5, y: -.25, traceorder: 'reversed',
                        bgcolor: isAccumulated ? 'rgba(100,100,100,.25)' : 'rgba(100,100,100,.1)',
                        font: {size: 10}
                    };
                    layout.height = 350;
                }
                if (data.length > 2) {
                    data[2].type = 'scatter';
                    data[2].mode = 'lines';
                    generateXLastPoint(data[2]);
                    generateYLastPoint(data[2]);
                    data[2].showlegend = false;
                    data[2].hoverinfo = 'skip';
                    data[2].marker = {
                        color: 'rgba(100,100,100,.65)'
                    };
                    data[2].line = {width: 0};
                    data[2].fillcolor = 'rgba(100,100,100,.1)';
                    data[2].fill = 'none';
                    data[2].stackgroup = 'stdev';
                    data[2].legendgroup = data[1].legendgroup;
                    const plus = [], minus = [], avgText = [];
                    for (let i = 0; i < data[1].y.length; i++) {
                        const stdev = isAccumulated ? Math.sqrt(data[2].y[i]) : data[2].y[i];
                        const avg = data[1].y[i];
                        plus.push(stdev===null?null:stdev * 2);
                        minus.push(avg===null || stdev===null?null:Math.max(0, avg - stdev));
                        avgText.push(avg===null||stdev===null?null:(avg.toFixed(this.layer.getNrOfDecimals()) + ' (± ' + stdev.toFixed(this.layer.getNrOfDecimals()) + ')'));
                    }
                    data[2].y = minus;
                    data[1].text = avgText;
                    data[1].hovertemplate = '%{text}<extra></extra>';

                    data.push({
                        type: 'scatter',
                        mode: 'lines',
                        x: data[2].x,
                        y: plus,
                        showlegend: false,
                        hoverinfo: 'skip',
                        marker: data[2].marker,
                        line: data[2].line,
                        fill: 'tonexty',
                        fillcolor: isAccumulated ? 'rgba(100,100,100,.25)' : 'rgba(100,100,100,.1)',
                        stackgroup: data[2].stackgroup,
                        legendgroup: data[2].legendgroup,
                    });

                    let tmp = data[1];
                    data[1] = data[2];
                    data[2] = tmp;

                }
            }
            return data.slice(0, 4);
        }

        const render = (data, layout, config, chartTarget) => {
            if (!data)
                return;
            Plotly.newPlot(chartTarget[0], data, layout, config);
            new ResizeObserver(() => this._relayout()).observe(chartTarget[0]);
            setTimeout(() => {
                this._relayout();
                this.renderedChart = true;
            }, Math.random() * 100);
        }

        if (chartType === IndicatorChartTypeEnum.BARS || isBarsWithLine) {
            data = handleBarData(data, layout);
        } else if (isStackedBars) {
            const stackedData = [];
            layout.barmode = 'stack';
            layout.yaxis.tickvals = [0, 20, 40, 60, 80, 100];
            layout.yaxis.fixedrange = true;
            layout.yaxis.showgrid = false;
            layout.xaxis.showgrid = false;
            layout.xaxis.range = [
                this.layer.getTimeCalculator().getPreviousDate(data[0].x[0]),
                this.layer.getTimeCalculator().getNextDate(arrayLast(data[0].x)),
            ];

            //this is the way to be able to control the order of the traces on the hover
            layout.legend = {traceorder: 'reversed', y: .5, x: .5, orientation: 'v'};
            layout.showlegend = true;

            Object.keys(data[0].colors).forEach(label => {
                const stackedDataElement = {
                    x: data[0].x,
                    width: width,
                    offset: offset,
                    y: [],
                    showlegend: false,
                    name: label,
                    type: 'bar',
                    barmode: 'stack',
                    sort: false,
                    marker: {
                        color: data[0].colors[label]
                    },
                    hovertemplate: "%{y}%"
                }
                data[0].y.forEach(percentages => {
                    stackedDataElement.y.push(percentages[label]);
                });
                stackedData.push(stackedDataElement);
            })

            const ordinals = data[0].ordinals;
            data = stackedData;
            stackedData.sort((sd1, sd2) => ordinals[sd1.name] - ordinals[sd2.name]);
        }

        if (accumulatedData) {
            const valuesChartTarget = $('.container .body .chart-container.values', this.uiElement);
            render(data, layout, config, valuesChartTarget);

            const accumulatedLayout = clone(layout);
            accumulatedData[0].offset = offset;
            accumulatedData[0].width = width;
            accumulatedData = handleBarData(accumulatedData, accumulatedLayout, true);
            if (!accumulatedData)
                return;
            const accumulatedChartTarget = $('.container .body .chart-container.accumulated', this.uiElement).empty();
            render(accumulatedData, accumulatedLayout, config, accumulatedChartTarget);
            accumulatedChartTarget.append(accumulatedData[0].legend.getChartGraphic());

            if (isBarsWithLine) {
                accumulatedChartTarget.addClass('bars-with-line');
                valuesChartTarget.addClass('bars-with-line');
            }
        } else {
            const target = $('.container .body .chart-container', this.uiElement);
            if (isBarsWithLine) {
                target.addClass('bars-with-line');
            }
            render(data, layout, config, target);
        }

        const body = $('.container .body', this.uiElement);
        if (isRandomData)
            body.addClass('random');
        else body.removeClass('random');
        this._addMainLegend();
    }

    /**
     * Get the name for the stats data series (will be shown to the user)
     * (should be overridden)
     * @param isAccumulated {boolean}
     */
    getStatsSeriesName(isAccumulated = false) {
    }

    /**
     * Add the main legend to the container
     * @protected
     */
    _addMainLegend() {
        const body = $('.container .body', this.uiElement);
        const smallGraphic = this.layer.getChartLegend() ? this.layer.getChartLegend().getChartGraphic() : undefined;
        if (smallGraphic && smallGraphic.length > 0) {
            smallGraphic.click(() => this.layer.showInfo(this.infoModal)).css('cursor', 'pointer');
            $('.container .body .legend-container', this.uiElement).append(smallGraphic);
            body.addClass('has-legend');
        } else {
            body.removeClass('has-legend');
        }
    }

    /**
     * Given an array of dates (for x-axis), return an array of extentsin milliseconds
     * @param x {Array<Date>}
     * @return {Array<number>} integers
     * @protected
     */
    _calcBarWidth(x) {
        let lastDate = undefined;
        const width = [];

        x.forEach(date => {
            if (lastDate) {
                width.push(Math.round((date - lastDate) / 1000 / 60 / 60 / 24) * 1000 * 60 * 60 * 24); //round to days
            }
            lastDate = date;
        });

        const lastPeriodStart = arrayLast(x);
        const timeCalculator = this.layer.getTimeCalculator();

        let lastWidth = 0;
        switch (timeCalculator.getPeriodicity()) {
            case PeriodicityEnum.DAILY:
            case PeriodicityEnum.DAILY_YEARLESS:
                lastWidth = 24 * 60 * 60 * 1000;
                break;
            case PeriodicityEnum.WEEKLY:
            case PeriodicityEnum.ISO_WEEKLY:
            case PeriodicityEnum.TEN_DAILY:
            case PeriodicityEnum.TEN_DAILY_YEARLESS:
            case PeriodicityEnum.YEARLY:
            case PeriodicityEnum.MONTHLY:
            case PeriodicityEnum.MONTHLY_FORECAST:
                lastWidth = timeCalculator.getEndDateOfPeriod(timeCalculator.getNextDate(lastPeriodStart)) - lastPeriodStart;
                break;
            case PeriodicityEnum.TEN_DAILY_YEARLESS_30DAY:
            case PeriodicityEnum.MONTHLY_UPDATED_TEN_DAILY:
                const tendailyCalculator = new TimeCalculator(PeriodicityEnum.TEN_DAILY);
                lastWidth = tendailyCalculator.getNextDate(tendailyCalculator.getNextDate(tendailyCalculator.getNextDate(lastPeriodStart))) - lastPeriodStart;
                break;
            default:
                break;
        }
        width.push(Math.round(lastWidth + 24 * 60 * 60 * 1000));
        return width;
    }

    /**
     * Get the current timescale for this indicator (if it has one)
     * @return {TimeScaleEnum|undefined}
     * @protected
     */
    _getTimeScale() {
        const sessionProp = this.getSessionProp(this.id + '_timescale');
        return sessionProp ? TimeScaleEnum.getForCode(sessionProp) : this.layer.getTimeScale();
    }

    /**
     * Get the current ten-day period for this indicator (if it has one)
     * @return {number|undefined}
     * @protected
     */
    _getTenDayPeriod() {
        const sessionProp = this.getSessionProp(this.id + '_tendays');
        return sessionProp ? Number(sessionProp) : this.layer.getTimeCalculator().getTenDaysFromDate(dateFromStr(this.layer.getCurrentTimeCode()));
    }

    /**
     * Set the current timescale for this indicator (if ot has one)
     * @param timeScale {TimeScaleEnum}
     * @protected
     */
    _setTimeScale(timeScale) {
        this.setSessionProp(this.id + '_timescale', timeScale.getCode());
        this._updateFigCaptionContent();
    }

    /**
     * Set the current ten-day period for this indicator (if ot has one)
     * @param tenDayPeriod {string}
     * @protected
     */
    _setTenDayPeriod(tenDayPeriod) {
        this.setSessionProp(this.id + '_tendays', tenDayPeriod);
        this._updateFigCaptionContent();
    }

    _updateFigCaptionContent() {
        $('figure figcaption', this.uiElement).empty().append($(
            '<p>Chart for ' + this.layer.getName() + ': From ' + formatDate(this.from) + ' until ' + formatDate(this.layer.getTimeCalculator().fixDateUntil(this.until)) + '</p>' +
            '<p>Data in CSV format can be downloaded from the download button</p>'
        ));
    }

    getContainerElement() {
        const expanded = this.isExpanded();
        const elements = $(
            '<figure>' +
            '   <figcaption id="figcaption_' + this.layer.getCode() + '"></figcaption>' +
            '   <div class="chart container ' + (expanded ? 'expanded' : '') + '">' +
            '      <div class="header" role="heading">' +
            '          <div class="title"><h3>' + this.getChartTitle() + '</h3></div>' +
            '          <div class="buttons-container">' +
            '              <button class="expander ' + (expanded ? 'collapse' : 'expand') + '" type="button" title="' + this.translator.translate('tool.charts.expander') + '"></button>' +
            '          </div>' +
            '      </div>' +
            '      <div class="body" aria-expanded="' + expanded + '">' +
            '         <div class="simple-stats-container"></div>' +
            '         <div class="chart-container"></div>' +
            '         <div class="legend-container"></div>' +
            '         <div class="indicator-buttons-container"></div>' +
            '         <div class="random-warning">[Random data]</div>' +
            '      </div>' +
            '   </div>' +
            '</figure>'
        );

        this.loader = new Loader('loader_' + this.layer.getCode(), this.isMobileDevice, $('.body', elements));

        const timeScales = this.layer.getServiceConfig().getTimeScales();
        if (timeScales && timeScales.length > 1) {
            const currentTimeScale = this._getTimeScale() ? this._getTimeScale() : timeScales[0];
            const timeScaleDropdownId = this.getCode() + '_report_timeScale_dropdown';
            const timeScaleContainer = $(
                '<div class="dropdown-container time-scale-container"><label for="' + timeScaleDropdownId + '"> | ' + this.translator.translate('layer.timescale.dropdown.label') + '</label></div>'
            );
            const timeScaleOptions = timeScales.map(ts => {
                return new DropdownOption(this.translator, this.isMobileDevice, ts.getLocalizationKey(), ts.getCode(), undefined)
            });
            this.timeScaleDropdown = new Dropdown(this.translator, this.isMobileDevice, timeScaleDropdownId, timeScaleOptions, currentTimeScale ? currentTimeScale.getCode() : undefined, true, undefined,
                dropDown => {
                    const timeScale = TimeScaleEnum.getForCode(dropDown.getValue());
                    if (this._getTimeScale().getCode() !== timeScale.getCode()) {
                        this._setTimeScale(timeScale);
                        $('.header .title h3', this.uiElement).html(this.layer.getNameForTimeScale(timeScale));
                        this._updateChart();
                    }
                }, undefined);
            timeScaleContainer.append(this.timeScaleDropdown.getUIElement());
            $('.header', elements).append(timeScaleContainer);
        }

        if (this.layer.getTimeCalculator().isMonthlyUpdatedTenDaily() || this.layer.getTimeCalculator().isTenDailyYearless30Day()) {
            const tendDaysDropdownId = this.getCode() + '_report_tendays_dropdown';
            const tenDaysScaleContainer = $(
                '<div class="dropdown-container ten-days-container"><label for="' + tendDaysDropdownId + '"> | ' + this.translator.translate('layer.tenday.slider.label') + '</label></div>'
            );
            const tenDaysOptions = [
                {name: 'layer.tenday.slider.options.first', code: '1'},
                {name: 'layer.tenday.slider.options.second', code: '2'},
                {name: 'layer.tenday.slider.options.third', code: '3'}
            ].map(ts => {
                return new DropdownOption(this.translator, this.isMobileDevice, ts.name, ts.code, undefined)
            });
            this.tenDaysDropdown = new Dropdown(this.translator, this.isMobileDevice, tendDaysDropdownId, tenDaysOptions, this._getTenDayPeriod() + "", true, undefined,
                dropDown => {
                    if (this._getTenDayPeriod() + "" !== dropDown.getValue()) {
                        this._setTenDayPeriod(dropDown.getValue());
                        this._updateChart();
                    }
                }, undefined);
            tenDaysScaleContainer.append(this.tenDaysDropdown.getUIElement());
            $('.header', elements).append(tenDaysScaleContainer);
        }
        return elements;
    }

    getChartTitle() {
        const timeScales = this.layer.getServiceConfig().getTimeScales();
        const currentTimeScale = this._getTimeScale() ? this._getTimeScale() : timeScales[0];
        return this.layer.getNameForTimeScale(currentTimeScale);
    }

    getUIElement() {
        const uiElement = super.getUIElement();
        return uiElement
            .attr('aria-labelledby', 'figcaption_' + this.layer.getCode())
            .attr('role', 'figure');
    }

    _downloadData(productCode) {
        const rows = []
        if (this.layer.getChartType() === IndicatorChartTypeEnum.STACKED_BARS) {
            const keys = Object.keys(this.yValues[0]);
            rows.push(['date'].concat(keys));
            for (let i = 0; i < this.xValues.length; i++) {
                const y = keys.map(k => this.yValues[i][k]);
                rows.push([formatDate(this.xValues[i])].concat(y));
            }
        } else {
            rows.push(['date', 'value']);
            if (this.yAvgValues)
                rows[0].push('avg');
            if (this.yStdValues)
                rows[0].push('std');
            if (this.yAccumValues)
                rows[0].push('cumulative');
            if (this.yAccumAvgValues)
                rows[0].push('cumulative_avg');
            if (this.yAccumStdValues)
                rows[0].push('cumulative_std');
            for (let i = 0; i < this.xValues.length; i++) {
                const y = []
                y.push(this.yValues[i]);
                if (this.yAvgValues)
                    y.push(this.yAvgValues[i]);
                if (this.yStdValues)
                    y.push(this.yStdValues[i]);
                if (this.yAccumValues)
                    y.push(this.yAccumValues[i]);
                if (this.yAccumAvgValues)
                    y.push(this.yAccumAvgValues[i]);
                if (this.yAccumStdValues)
                    y.push(this.yAccumStdValues[i]);
                rows.push([formatDate(this.xValues[i])].concat(y));
            }
        }
        const encodedUri = encodeURI("data:text/csv;charset=utf-8," + rows.map(r => r.join(',')).join("\n"));
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", productCode + "_" + formatDate(this.from) + "_" + formatDate(this.until) + ".csv");
        document.body.appendChild(link); // Required for FF
        link.click();
        link.remove();
        this.logStat("DOWNLOAD_CSV")
    }
}

class FeatureIndicatorChart extends IndicatorChart {

    getClassName(){
        return "FeatureIndicatorChart";
    }

    /**
     *
     * @param id {string} An HTML id for this indicator chart
     * @param featureName {string} The name of the feature being charted
     * @param translator {Translator} A translator
     * @param layer {Layer} The indicator of this chart
     * @param infoModal {Modal} A modal window to show indicator metadata in
     * @param runtimeConfig {RuntimeConfig} The runtime config
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     */
    constructor(id, featureName, translator, layer, infoModal, runtimeConfig, isMobileDevice) {
        super(id, translator, undefined, layer, infoModal, runtimeConfig, isMobileDevice);
        this.featureName = featureName;
    }


    getChartTitle() {
        return super.getChartTitle() + (this.featureName ? ' - ' + this.featureName : '');
    }

    /**
     * @param from {Date}
     * @param until {Date}
     * @param wgs84Coords {Array<number>} 2 position array with coords lon lat in wgs84
     * @param featureProps {Object} OL Feature properties
     */
    setFromUntilAndCoords(from, until, wgs84Coords, featureProps) {
        if (from) {
            this.from = this.layer.getTimeCalculator().fixDate(dateFromStr(from));
            this.unboundedFrom = dateFromStr(from);
        }
        if (until) {
            this.until = this.layer.getTimeCalculator().fixUnboundedDateUntil(dateFromStr(until));
            this.unboundedUntil = dateFromStr(until);
        }
        if (!this.wgs84Coords)
            this.wgs84Coords = wgs84Coords;
        if (!this.featureProps) {
            this.featureProps = featureProps;
            this.timeseriesFetcher = this.layer.getTimeseriesFetcherForFeature(this.featureProps);
        }
        this._updateChart();
    }
}

class IndicatorWithAccumulationChart extends IndicatorChart {

    getClassName(){
        return "IndicatorWithAccumulationChart";
    }

    _addMainLegend() {
        const body = $('.container .body', this.uiElement);
        const smallGraphic = this.layer.getChartLegend() ? this.layer.getChartLegend().getChartGraphic() : undefined;
        if (smallGraphic && smallGraphic.length > 0) {
            smallGraphic.click(() => this.layer.showInfo(this.infoModal)).css('cursor', 'pointer');
            $('.container .body .chart-container.values', this.uiElement).append(smallGraphic);
        }
        body.removeClass('has-legend');
        $(body.children('.legend-container')[0]).remove();
    }

}

export {IndicatorChart, FeatureIndicatorChart, IndicatorWithAccumulationChart}